import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FolderKanban, Clock, CheckCircle2, Edit2, Save, X, Sparkles, UserCheck } from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG, ProjectStatus, MilestoneStatus } from '@ftt/shared';

export const AdminProjectsPage: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projectStatus, setProjectStatus] = useState<string>('');
  const [targetPubDate, setTargetPubDate] = useState<string>('');
  const [assignedStaffId, setAssignedStaffId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const queryClient = useQueryClient();

  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: () => api.get('/admin/projects').then((res) => res.data.data),
  });

  const { data: staffRes } = useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => api.get('/admin/staff').then((res) => res.data.data),
  });

  const staffList = staffRes || [];
  const projects = projectsRes || [];

  const updateProjectMutation = useMutation({
    mutationFn: async () => {
      return api.put(`/admin/projects/${selectedProject.id}`, {
        status: projectStatus,
        targetPubDate: targetPubDate || null,
        assignedStaffId: assignedStaffId || null,
        notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      setSelectedProject(null);
    },
  });

  const updateMilestoneMutation = useMutation({
    mutationFn: async ({ milestoneId, status }: { milestoneId: string; status: MilestoneStatus }) => {
      return api.put(`/admin/milestones/${milestoneId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
    },
  });

  const openProjectEditor = (p: any) => {
    setSelectedProject(p);
    setProjectStatus(p.status);
    setTargetPubDate(p.targetPubDate ? p.targetPubDate.substring(0, 10) : '');
    setAssignedStaffId(p.assignedStaffId || '');
    setNotes(p.notes || '');
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              OPERATIONS PIPELINE
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <FolderKanban className="w-7 h-7 text-[#FF6B4A]" />
            <span>Publishing Project Management</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Supervise project lifecycles, advance 12-stage milestones, and allocate senior editors.
          </p>
        </div>

        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono font-bold text-slate-700">
          <span>Active Projects: <strong className="text-slate-900">{projects.length}</strong></span>
        </div>
      </div>

      {/* 2. Projects Grid */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200">
          Loading projects...
        </div>
      ) : projects.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <FolderKanban className="w-12 h-12 text-slate-400 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Projects In Pipeline</h3>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((proj: any) => {
            const completedCount = proj.milestones?.filter((m: any) => m.status === 'COMPLETED').length || 0;
            const totalCount = proj.milestones?.length || 12;
            const pct = Math.round((completedCount / totalCount) * 100);

            return (
              <div
                key={proj.id}
                className="p-6 sm:p-8 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-6 shadow-sm"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono px-2.5 py-0.5 rounded-lg bg-slate-900 text-white font-bold">
                        {proj.projectCode}
                      </span>
                      <span className="text-xs text-slate-500 font-mono font-bold">
                        {proj.category}
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl font-black text-slate-900 pt-1">
                      {proj.title}
                    </h2>
                    <p className="text-xs text-slate-600 font-medium">
                      Author: <strong className="text-slate-900">{proj.author}</strong> &bull; Client: {proj.client?.user?.firstName || ''} {proj.client?.user?.lastName || ''} ({proj.client?.user?.email || 'Author Account'})
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openProjectEditor(proj)}
                      className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-[#FF6B4A]" />
                      <span>Edit Status &amp; Lead</span>
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono font-bold text-slate-700">
                    <span>
                      12-Stage Milestones ({completedCount} of {totalCount} Completed)
                    </span>
                    <span className="text-brand-700 font-black">{pct}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200">
                    <div className="bg-[#EA580C] h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                  </div>
                </div>

                {/* Quick Milestones Checklist */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                  {proj.milestones?.map((m: any) => {
                    const isDone = m.status === 'COMPLETED';
                    const isInProg = m.status === 'IN_PROGRESS';
                    return (
                      <div
                        key={m.id}
                        onClick={() => {
                          const nextSt: MilestoneStatus = isDone ? 'PENDING' : isInProg ? 'COMPLETED' : 'IN_PROGRESS';
                          updateMilestoneMutation.mutate({ milestoneId: m.id, status: nextSt });
                        }}
                        className={`p-3 rounded-xl border text-[11px] cursor-pointer transition-all ${
                          isDone
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                            : isInProg
                            ? 'bg-orange-50 border-orange-300 text-orange-950 ring-2 ring-[#FF6B4A]'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        title="Click to toggle milestone state"
                      >
                        <div className="font-mono text-[10px] font-bold">Stage {m.orderIndex}</div>
                        <div className="font-extrabold truncate mt-0.5 text-slate-900">{m.title}</div>
                        <div className="text-[9px] font-mono font-bold uppercase mt-1">
                          {isDone ? '✓ Done' : isInProg ? '● Active' : '○ Pending'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-serif text-xl font-bold text-slate-900">
                Project Settings: {selectedProject.projectCode}
              </h3>
              <button onClick={() => setSelectedProject(null)} className="text-slate-400 hover:text-slate-900 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Overall Project Status
                </label>
                <select
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                >
                  {PUBLISHING_STAGES_CONFIG.map((s) => (
                    <option key={s.status} value={s.status}>{s.order}. {s.label}</option>
                  ))}
                  <option value="COMPLETED">Completed &amp; Archived</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Assigned Senior Editor / Lead
                </label>
                <select
                  value={assignedStaffId}
                  onChange={(e) => setAssignedStaffId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                >
                  <option value="">Unassigned (Committee Pool)</option>
                  {staffList.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name || `${s.firstName} ${s.lastName}`} ({s.jobTitle || s.role})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Target Publication Date
                </label>
                <input
                  type="date"
                  value={targetPubDate}
                  onChange={(e) => setTargetPubDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Staff Notes
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal milestones notes..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setSelectedProject(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => updateProjectMutation.mutate()}
                disabled={updateProjectMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs cursor-pointer shadow-sm"
              >
                Update Project Status
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
